import { Sports } from '../constants/sportType';
import {
  CurrencyPosition,
  FansUnitedConfig,
  IAssets,
  IBaseTeam,
  IConfigDate,
  IConfigEntityLinks,
  ICountry,
  IElements,
  IFansUnitedDataLabels,
  IHighlightEntities,
  IKnockoutDataLabels,
  ILabels,
  ILivescoreDataLabels,
  IMatch,
  IMatchesH2HDataLabels,
  IMostDecoratedDataLabels,
  IPlayer,
  IPlayerDataElements,
  IPlayerDataLabels,
  IProgramDataLabels,
  ISingleEventDataLabels,
  ISportEntity,
  IStage,
  IStakesDataLabels,
  IStandingsDataLabels,
  ITabs,
  ITeamScore,
  ITeamSquadLabels,
  ITopScorersDataLabels,
  Markets,
  RefreshTimes,
  ShortNameType,
  ShortStatusType,
  Themes,
  WidgetType,
} from './common';

export type IFootballConfig = IFootballTimelineConfig &
  IFootballMatchCenterConfig &
  IStakesConfig &
  IFootballLineupsConfig &
  IFootballOddsConfig &
  IFootballTeamH2hWidgetConfig &
  IFootballPlayerH2hConfig &
  ITeamSquadConfig &
  IStandingsConfig &
  ISingleEventConfig &
  IKnockoutConfig &
  IPlayerConfig &
  ITeamConfig &
  FansUnitedConfig &
  ITopScorersConfig &
  IMostDecoratedConfig &
  IMatchesH2HConfig &
  IPlayerStatisticsConfig &
  IRoundConfig;

interface IGeneralWidgetConfig {
  dataWidgetId: string;
  dataWidgetSport: Sports;
  dataWidgetType: WidgetType;
  dataTheme?: Themes | null;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataRefreshTime?: RefreshTimes;
  dataLabels?: ILabels;
}

export enum NameFormat {
  LAST_NAME = 'last_name',
  INITIALS = 'initials',
}

export interface IFootballLineupsConfig {
  dataWidgetId: string;
  dataWidgetSport: Sports;
  dataWidgetType: WidgetType;
  dataMatchId: IMatch['id'];
  dataElements?: (string | { name: string; status: 'open' | 'closed' })[];
  dataEntityLinks?: IConfigEntityLinks | null;
  dataHeaderDisplay?: boolean;
  dataHomeTeamColor?: string;
  dataAwayTeamColor?: string;
  dataLabels?: { first_team?: string; substitutes?: string; no_data?: string } & ILabels;
  dataRefreshTime?: RefreshTimes;
  dataTheme?: Themes | null;
  dataNameFormat?: NameFormat;
}

export interface IFootballMatchCenterConfig extends IGeneralWidgetConfig {
  dataMatchId: IMatch['id'];
  dataTeamNameType?: ShortNameType | null;
  dataOddsDisplay?: boolean;
  dataBettingLogoClickAction?: boolean;
  dataBettingLogoOverlap?: boolean;
  dataOddsBettingId?: string | null;
  dataOddsTabBettingId?: string | null;
  dataOddsMarket?: Markets;
  dataOddsMarketValueType?: FootballOddMarketValueTypes;
  dataOddsScopeType?: ScopeTypes;
  dataOddsPreEventOnly?: boolean;
  dataMatchCenterMainEventsDisplay?: boolean;
  dataNameFormat?: NameFormat;
}

export interface IFootballTimelineConfig extends IGeneralWidgetConfig {
  dataMatchId: IMatch['id'];
  widgetStartTime?: string | undefined; // this prop will be used only in the context of match widget, otherwise we should execute additional request to get the match start time
}

export interface IFootballOddsConfig {
  dataWidgetId: string;
  dataWidgetSport: Sports;
  dataWidgetType: WidgetType;
  dataMatchId: IMatch['id'] | undefined;
  dataLabels?: { no_odds?: string } & ILabels;
  dataOddsBettingId?: string | null;
  dataOddsMarket?: Markets;
  dataOddsMarketValueType?: FootballOddMarketValueTypes;
  dataOddsScopeType?: ScopeTypes;
  dataOddsPreEventOnly?: boolean;
  dataRefreshTime?: RefreshTimes;
  dataTheme?: Themes | null;
  dataBettingBrandColors?: boolean;
  dataBettingLogoClickAction?: boolean;
  dataBettingLogoOverlap?: boolean;
}

export interface IStakesConfig {
  dataWidgetId: string;
  dataWidgetSport: Sports;
  dataWidgetType: WidgetType;
  dataMatchId: IMatch['id'];
  dataAmount?: number;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataFixed?: boolean;
  dataLabels?: IStakesDataLabels & ILabels;
  dataOddsBettingId?: string | null;
  dataOddsPreEventOnly?: boolean;
  dataRefreshTime?: RefreshTimes;
  dataTheme?: Themes | null;
  dataCurrencySymbolPosition?: CurrencyPosition;
}

export interface IFootballTeamH2hWidgetConfig {
  dataRefreshTime?: RefreshTimes;
  dataWidgetId: string;
  dataWidgetSport: Sports;
  dataWidgetType: WidgetType;
  dataSportEntityOne: ISportEntity;
  dataSportEntityTwo: ISportEntity;
  dataElements?: string[];
  dataEntityLinks?: IConfigEntityLinks | null;
  dataHeaderDisplay?: boolean;
  dataLabels?: { odds?: string } & ILabels;
  dataTheme?: Themes | null;
}

export interface IFootballTeamMatchH2hWidgetConfig {
  dataWidgetId: string;
  dataWidgetSport: Sports;
  dataWidgetType: WidgetType;
  dataSportEntityOne: ISportEntity;
  dataSportEntityTwo: ISportEntity;
  dataMatchId: IMatch['id'];
  dataElements?: IElements;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataHeaderDisplay?: boolean;
  dataLabels?: { odds?: string } & ILabels;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsMarket?: Markets;
  dataOddsMarketValueType?: FootballOddMarketValueTypes;
  dataOddsScopeType?: ScopeTypes;
  dataOddsPreEventOnly?: boolean;
  dataRefreshTime?: RefreshTimes;
  dataTheme?: Themes | null;
}

export interface IFootballPlayerH2hConfig {
  dataRefreshTime?: RefreshTimes;
  dataWidgetId: string;
  dataWidgetSport: Sports;
  dataWidgetType: WidgetType;
  dataSportEntityOne: ISportEntity;
  dataSportEntityTwo: ISportEntity;
  dataElements?: string[];
  dataEntityLinks?: IConfigEntityLinks | null;
  dataHeaderDisplay?: boolean;
  dataLabels?: { vs?: string } & ILabels;
  dataTheme?: Themes | null;
}

export interface ITeamSquadConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataTheme?: Themes | null;
  dataTeam?: string;
  dataSeason?: string;
  dataCompetition?: string;
  dataTeamNameType?: ShortNameType | null;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataHeaderDisplay?: boolean;
  dataLabels?: ITeamSquadLabels | null;
  dataHeaderDefaultOption?: string;
  dataElements?: IElements;
  dataOptionsDisplay?: boolean;
  dataImageDisplay?: boolean;
}

export interface ILivescoreConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataDate?: IConfigDate | null;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsMarket?: Markets;
  dataOddsPreEventOnly?: boolean;
  dataOddsMarketValueType?: FootballOddMarketValueTypes;
  dataOddsScopeType?: ScopeTypes;
  dataTeamShortNameType?: ShortNameType | null;
  dataTeamNameType?: ShortNameType | null;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataPopularList?: string;
  dataLabels?: ILivescoreDataLabels | null;
  dataHeaderDisplay?: boolean;
  dataOptionsDisplay?: boolean;
  dataHeaderDefaultOption?: string;
  dataAllowInlineOdds?: boolean;
  dataBettingBrandColors?: boolean;
  dataBettingLogoClickAction?: boolean;
  dataBettingLogoOverlap?: boolean;
}

export interface IStandingsConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataTeamNameType?: ShortNameType | null;
  dataStage?: string;
  dataSeason?: string;
  dataCompetition?: string;
  dataHeaderDisplay?: boolean;
  dataOptionsDisplay?: boolean;
  dataGroups?: number[];
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataLabels?: IStandingsDataLabels | null;
  dataElementsDesktop?: string[];
  dataElementsMobile?: string[];
  dataImageDisplay?: boolean;
  dataHighlightEntities?: IHighlightEntities;
  dataOffset?: number;
  dataLimit?: string;
}

export interface IKnockoutConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataTeamNameType?: ShortNameType | null;
  dataStage?: string;
  dataSeason?: string;
  dataCompetition?: string;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataLabels?: IKnockoutDataLabels | null;
}

export interface IMatchesH2HConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataSportEntityOne: string;
  dataSportEntityTwo: string;
  dataMatchType?: string;
  dataLimit?: string;
  dataTeamNameType?: ShortNameType | null;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataLabels?: IMatchesH2HDataLabels;
  dataOptionsDisplay?: boolean;
  dataHeaderDefaultOption?: string;
}

export interface ISingleEventConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataMatchId?: string;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsMarket?: Markets;
  dataOddsPreEventOnly?: boolean;
  dataOddsMarketValueType?: FootballOddMarketValueTypes;
  dataOddsScopeType?: ScopeTypes;
  dataTeamShortNameType?: ShortNameType | null;
  dataTeamNameType?: ShortNameType | null;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataShortStatusType?: ShortStatusType | null;
  dataTheme?: Themes | null;
  dataLabels?: ISingleEventDataLabels & IFansUnitedDataLabels;
  dataAdditionalInfo?: boolean;
  dataWrapperLink?: boolean;
  dataBettingBrandColors?: boolean;
  dataBettingLogoClickAction?: boolean;
  dataBettingLogoOverlap?: boolean;
  dataSingleEventMainEventsDisplay?: boolean;
  dataNameFormat?: NameFormat;
}

export interface IPlayerConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataTeamNameType?: ShortNameType | null;
  dataShortStatusType?: ShortStatusType | null;
  dataPlayer?: string;
  dataTeam?: string;
  dataSeason?: string;
  dataCompetition?: string;
  dataMatchId?: string;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataLabels?: IPlayerDataLabels & ILabels;
  dataTabs?: ITabs;
  dataDisplayTabs?: boolean;
  dataDefaultTab?: string;
  dataElements?: IElements;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsMarket?: Markets;
  dataOddsPreEventOnly?: boolean;
  dataOddsMarketValueType?: FootballOddMarketValueTypes;
  dataOddsScopeType?: ScopeTypes;
  dataDateFormat?: string;
}

export interface IPlayerStatisticsConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataTeamNameType?: ShortNameType | null;
  dataPlayer?: string;
  dataSeason?: string;
  dataMatchId?: string;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataLabels?: IPlayerDataLabels & ILabels;
  dataElements?: IPlayerDataElements;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsMarket?: Markets;
  dataOddsPreEventOnly?: boolean;
  dataOddsMarketValueType?: FootballOddMarketValueTypes;
  dataOddsScopeType?: ScopeTypes;
  dataMatchDisplay?: boolean;
  dataStatisticsDisplay?: boolean;
  dataDateFormat?: string;
  dataDisplaySocialIcons?: boolean;
  dataAllowInlineOdds?: boolean;
  dataBettingBrandColors?: boolean;
  dataBettingLogoClickAction?: boolean;
  dataBettingLogoOverlap?: boolean;
}

export interface IMostDecoratedConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataSeason?: string;
  dataCompetition?: string;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataLabels?: IMostDecoratedDataLabels | null;
  dataHighlightEntities: IHighlightEntities;
  dataTeamNameType?: ShortNameType | null;
  dataHeaderDisplay?: boolean;
  dataLimit?: string;
  dataStartPosition?: string;
  dataElements?: IElements;
  dataImageDisplay?: boolean;
}

export interface ITeamConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataTeamNameType?: ShortNameType | null;
  dataShortStatusType?: ShortStatusType | null;
  dataTeam?: string;
  dataSeason?: string;
  dataMatchId?: string;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataLabels?: IPlayerDataLabels & ILabels;
  dataElements?: IPlayerDataElements;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsMarket?: Markets;
  dataOddsPreEventOnly?: boolean;
  dataOddsMarketValueType?: FootballOddMarketValueTypes;
  dataOddsScopeType?: ScopeTypes;
  dataMatchDisplay?: boolean;
  dataStatisticsDisplay?: boolean;
  dataDisplaySocialIcons?: boolean;
  dataDisplayTeamImage?: boolean;
  dataAllowInlineOdds?: boolean;
  dataBettingBrandColors?: boolean;
  dataBettingLogoClickAction?: boolean;
  dataBettingLogoOverlap?: boolean;
}

export interface ITopScorersConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataSeason?: string;
  dataCompetition?: string;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataLabels?: ITopScorersDataLabels | null;
  dataTeamNameType?: ShortNameType | null;
  dataTeamIds?: string[];
  dataHighlightEntities?: IHighlightEntities;
  dataHeaderDisplay?: boolean;
  dataLimit?: number;
  dataStartPosition?: number;
  dataElements?: IElements;
  dataImageDisplay?: boolean;
}

export interface IRoundConfig {
  dataWidgetSport: string;
  dataWidgetType: WidgetType;
  dataWidgetId: string;
  dataSeason?: string;
  dataStage?: string;
  dataRound?: string;
  dataLimit?: string;
  dataCompetition: string;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsMarket?: Markets;
  dataOddsPreEventOnly?: boolean;
  dataOddsMarketValueType?: FootballOddMarketValueTypes;
  dataOddsScopeType?: ScopeTypes;
  dataTeamNameType?: ShortNameType | null;
  dataRefreshTime: RefreshTimes;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataTheme?: Themes | null;
  dataLabels?: IProgramDataLabels;
  dataHeaderDisplay?: boolean;
  dataDropdownDisplay?: boolean;
  dataAllowInlineOdds?: boolean;
  dataSortDirection?: string;
  dataBettingBrandColors?: boolean;
  dataBettingLogoClickAction?: boolean;
  dataBettingLogoOverlap?: boolean;
  dataDateFrom?: IConfigDate;
  dataDateTo?: IConfigDate;
}

export type FootballLivescoreMatchStatus = 'upcoming' | 'live' | 'finished' | 'popular';
export type FootballOddMarketValueTypes = 'fractional' | 'moneyline' | 'decimal';

export enum ScopeTypes {
  FIRST_HALF = 'FIRST_HALF',
  SECOND_HALF = 'SECOND_HALF',
  ORDINARY_TIME = 'ORDINARY_TIME',
  FULL_TIME = 'FULL_TIME',
  ALL = 'ALL',
}

export interface ITeamStatistics {
  corners?: number | null;
  counterAttacks?: number | null;
  crosses?: number | null;
  foulsCommitted?: number | null;
  goalKicks?: number | null;
  goals?: number | null;
  offside?: number | null;
  possession?: number | null;
  redCards?: number | null;
  yellowCards?: number | null;
  shotsBlocked?: number | null;
  shotsOff?: number | null;
  shotsOn?: number | null;
  substitutions?: number | null;
  throwIn?: number | null;
  treatments?: number | null;
}

export interface ITeamMatchStatistics {
  eventId: string;
  team: IBaseTeam;
  isHomeTeam: boolean;
  statistics: ITeamStatistics;
  updatedAt: string;
}

export interface ILineups {
  awayTeam: IMatchTeamLineup;
  homeTeam: IMatchTeamLineup;
  matchId: string;
  status: LineupsStatus;
}

export enum LineupsStatus {
  CONFIRMED = 'CONFIRMED',
  UNCONFIRMED = 'UNCONFIRMED',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

export interface IMatchTeamLineup {
  teamId: string;
  players: IMatchPlayerLineup[];
  coach: ICoach | null;
  formation: string;
}

export interface IMatchPlayerLineup {
  player: IPlayer;
  positionX: number | null;
  positionY: number | null;
  shirtNumber: number;
  type: IType;
  events: IMatchEvent[];
}

export enum EventTypeCode {
  YELLOW_CARD_RED = 'YELLOW_RED_CARD',
  PENALTY_SHOOTOUT_MISSED = 'PENALTY_SHOOTOUT_MISSED',
  PENALTY_SHOOTOUT_SCORED = 'PENALTY_SHOOTOUT_SCORED',
  SUBSTITUTION = 'SUBSTITUTION',
  GOAL = 'GOAL',
  RED_CARD = 'RED_CARD',
  PENALTY_MISS = 'PENALTY_MISS',
  YELLOW_CARD = 'YELLOW_CARD',
  ASSIST = 'ASSIST',
  PENALTY_GOAL = 'PENALTY_GOAL',
  OWN_GOAL = 'OWN_GOAL',
  DISALLOWED_GOAL = 'DISALLOWED_GOAL',
  DISALLOWED_MISSED_PENALTY = 'DISALLOWED_MISSED_PENALTY',
  DISALLOWED_PENALTY = 'DISALLOWED_PENALTY',
  DISALLOWED_OWN_GOAL = 'DISALLOWED_OWN_GOAL',
  KICKOFF = 'KICKOFF',
  HALFTIME = 'HALFTIME',
  START_SECOND_HALF = 'START_SECOND_HALF',
  FINISHED_FIRST_HALF = 'FINISHED_FIRST_HALF',
  FINISHED_AFTER_EXTRA_TIME = 'FINISHED_AFTER_EXTRA_TIME',
  FINISHED_AFTER_PENALTIES = 'FINISHED_AFTER_PENALTIES',
  OVERTIME_FIRST_HALF = 'OVERTIME_FIRST_HALF',
  OVERTIME_FIRST_HALF_END = 'OVERTIME_FIRST_HALF_END',
  OVERTIME_SECOND_HALF = 'OVERTIME_SECOND_HALF',
  INJURY_TIME = 'INJURY_TIME',
  FINISHED = 'FINISHED',
}

export interface IType {
  id: string;
  name: string;
  code: string;
  category: string;
}

export interface ICoach {
  id: string;
  name: string;
  country: ICountry;
  birthdate: string;
  gender: string;
  assets: IAssets;
  slug: string;
}

export interface IMatchEvent {
  id: string;
  matchId: string;
  typeCode: EventTypeCode;
  teamPosition: string;
  minute: number;
  teamId: string;
  primaryPlayer: IPlayer;
  secondaryPlayer: IPlayer;
  score?: ITeamScore;
}

export interface IMatchEventSnakeCase {
  id: string;
  matchId: string;
  type_code: EventTypeCode;
  team_position: string;
  minute: number;
  teamId: string;
  primary_player: IPlayer;
  secondary_player: IPlayer;
  score?: ITeamScore;
  injury_minute?: number;
  injury_time_minutes?: number;
}

export interface IRounds {
  stage: IStage;
  rounds: {
    label: string;
    value: string;
  }[];
}

export interface ITeamProfileStatistics {
  goals_conceded?: number | null;
  goals_scored?: number | null;
  defeats?: number | null;
  draw?: number | null;
  win?: number | null;
  played?: number | null;
  rank?: number | null;
  points?: number | null;
}

export interface IFootballTeam {
  country: {
    id: number;
    name: string;
    slug: string;
    url_flag: string;
  };
  founded: number;
  gender: string;
  id: number;
  name: string;
  national: boolean;
  short_name: string | null;
  slug: string;
  social: {
    facebook_id: string | null;
    instagram_id: string | null;
    twitter_id: string | null;
    web: string | null;
    wikipedia_id: string | null;
  };
  three_letter_code: string | null;
  type: string;
  undecided: boolean;
  url_away_kit: string | null;
  url_home_kit: string | null;
  url_logo: string | null;
  url_squad_image: string | null;
}

export interface IFootballSeasonV2 {
  id: string;
  name: string;
  slug: string;
  tournament: IFootballTournamentV2;
  status: string;
  uuid: string;
}

export interface IFootballTournamentV2 {
  id: string;
  name: string;
  slug: string;
  country: IFootballCountryV2;
  assets: {
    logo: {
      url: string;
    };
  };
}

export interface IFootballCountryV2 {
  id: string;
  name: string;
  slug: string;
  assets: {
    flag: {
      url: string;
    };
  };
}
